import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { Fan, SendHorizontal } from "lucide-mui";
import { useAuth } from "../context/AuthContext";
import { useSSE } from "../context/SSEContext";
import { useTheme } from "../context/ThemeContext";
import { useDevLogger } from "../context/DevLogContext";
import { useNotification } from "../context/NotificationContext";
import { INTERPRETER_BASE_URL } from "../config/runtime";
import { cancelFileUpload } from "../api/filesApi";
import { sendChatMessage, getChatHistory } from "../api/chatApi";
import { ChatMessageComponent, AttachedFileChip } from "../components";

export default function ChatComponent({
  messages = [],
  updateMessages,
  updateMessageStatus,
  updateSkeletonStatus,
  sessionId,
  currentFolderId = null,
  onRefetch = () => {},
  handleTocAction = null,
  currentAnchorId = null,
  onStepChange = null,
  tabType = "data-manager",
  fileId = null,
  onViewModel = null,
  onToggleSidebar = null,
  attachedFilesInChat = [],
  onRemoveAttachedFile = () => {},
  onClearAttachedFiles = () => {},
  isFileDragOver = false,
  currentStep = null,
}) {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { addDevLog } = useDevLogger();
  const { addNotification } = useNotification();
  const { registerCallback } = useSSE();

  const [chatInput, setChatInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isProcedureEditing, setIsProcedureEditing] = useState(false);
  const messagesEndRef = useRef(null);

  const handleCancelUpload = async (fileId, requestId) => {
    if (!user?.id || !fileId) return;
    updateMessageStatus(sessionId, requestId, "CANCELING", null);
    try {
      await cancelFileUpload(user.id, sessionId, fileId);
    } catch (e) {
      console.error("CancelFileUpload failed", e);
      updateMessageStatus(
        sessionId,
        requestId,
        "INGESTION_FAILED",
        "Cancel failed",
      );
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── SSE: file ingestion status ──────────────────────────────────────────────
  useEffect(() => {
    const cleanup = registerCallback("FILE_INGESTION_STATUS", data => {
      if (data.message?.session_id !== sessionId) return;
      const { file_name, status, request_id, message, file_id, progress } =
        data.message;
      updateMessageStatus(
        sessionId,
        request_id,
        status,
        message,
        file_id,
        progress,
      );
      if (status === "INGESTION_COMPLETED") {
        addDevLog(`File "${file_name}" ingested successfully!`, "success");
        addNotification(`"${file_name}" uploaded successfully`, "success");
        onRefetch();
      } else if (status === "INGESTION_FAILED") {
        addDevLog(`File "${file_name}" ingestion failed`, "error");
        addNotification(`"${file_name}" failed to ingest`, "error");
      } else if (status === "FILE_DELETED") {
        addDevLog(`File "${file_name}" upload cancelled`, "info");
        onRefetch();
      }
    });
    return cleanup;
  }, [registerCallback, sessionId]);


  // ── SSE: update files (file list changed) ───────────────────────────────────
  useEffect(() => {
    const cleanup = registerCallback("UPDATE_FILES", data => {
      if (data.message?.session_id !== sessionId) return;
      onRefetch();
    });
    return cleanup;
  }, [registerCallback, sessionId, onRefetch]);

  // ── SSE: skeleton generated ─────────────────────────────────────────────────
  useEffect(() => {
    const cleanup = registerCallback(
      "SKELETON_GENERATED",
      skeleton_generated_payload => {
        if (sessionId !== skeleton_generated_payload.message?.session_id)
          return;
        const message = skeleton_generated_payload.message;

        if (message.action === "skeleton_generated") {
          updateMessages(sessionId, [
            {
              type: "AGENT_RESPONSE",
              value: {
                topic: "SKELETON_GENERATED",
                ...message,
              },
            },
          ]);
          updateSkeletonStatus?.(
            "skeleton_saved",
            sessionId,
            null,
            "validating_skeleton",
            null,
          );
        } else if (
          message.status === "skeleton_saved" ||
          message.status === "waiting_for_user_action" ||
          message.action === "COMPLETE"
        ) {
          const newStatus = message.status || "skeleton_saved";
          const updatedPlan = message.data?.plan ?? null;
          updateSkeletonStatus?.(
            newStatus,
            sessionId,
            message.request_id,
            null,
            updatedPlan,
          );
          const completionMessage = message.data?.completion_message;
          if (completionMessage) {
            updateMessages(sessionId, [
              {
                type: "AGENT_RESPONSE",
                value: { message: completionMessage },
              },
            ]);
          }
        } else if (message.action === "enable_procedure_editing") {
          setIsProcedureEditing(true);
        }

        setIsLoading(false);
      },
    );
    return cleanup;
  }, [registerCallback, sessionId]);

  // ── SSE: DPM query status ────────────────────────────────────────────────────
  useEffect(() => {
    const cleanup = registerCallback("DPM_QUERY_STATUS", data => {
      const msg = data.message;
      if (msg?.session_id !== sessionId) return;
      if (msg.action === "DPM_GENERATED") {
        updateSkeletonStatus?.("approved", sessionId, null);
        onRefetch();
        updateMessages(sessionId, [
          {
            type: "AGENT_RESPONSE",
            value: { topic: "DPM_QUERY_STATUS", ...msg },
          },
        ]);
      } else if (msg.action === "FAILED") {
        updateMessages(sessionId, [
          {
            type: "AGENT_RESPONSE",
            value: {
              topic: "DPM_QUERY_STATUS",
              action: "FAILED",
              failure_reason: msg.failure_reason,
              isError: true,
            },
          },
        ]);
        addDevLog(
          `DPM generation failed${msg.failure_reason ? ": " + msg.failure_reason : ""}`,
          "error",
        );
      }
      setIsLoading(false);
    });
    return cleanup;
  }, [registerCallback, sessionId]);

  // ── SSE: DPM step changed (agent-driven step navigation) ────────────────────
  useEffect(() => {
    const cleanup = registerCallback("DPM_STEP_CHANGED", data => {
      const msg = data.message;
      if (msg?.session_id !== sessionId) return;
      if (msg.current_step_index != null) {
        onStepChange?.(msg.current_step_index);
      }
    });
    return cleanup;
  }, [registerCallback, sessionId]);

  // ── SSE: agent options prompt (e.g. "What type of procedure is this?") ──────
  useEffect(() => {
    const cleanup = registerCallback("AGENT_OPTIONS_PROMPT", data => {
      const msg = data.message;
      if (msg?.session_id !== sessionId) return;
      updateMessages(sessionId, [
        {
          type: "AGENT_RESPONSE",
          value: { topic: "OPTIONS_PROMPT", answered: false, ...msg },
        },
      ]);
      setIsLoading(false);
    });
    return cleanup;
  }, [registerCallback, sessionId]);

  // ── Fetch previous chat history on mount ────────────────────────────────────
  useEffect(() => {
    if (!user?.id || !sessionId) return;
    const fetchHistory = async () => {
      try {
        const history = await getChatHistory(user.id, sessionId);
        if (history.length > 0) updateMessages(sessionId, history, true);
      } catch {
        // no-op — chat history is non-critical
      }
    };
    fetchHistory();
  }, [sessionId]);

  // ── Skeleton handlers ────────────────────────────────────────────────────────
  const handleApproveSkeleton = async (requestId, dpmUuid) => {
    if (!user?.id) return;
    updateSkeletonStatus?.("approving", sessionId, requestId);
    try {
      const fetchUrl = INTERPRETER_BASE_URL
        ? `${INTERPRETER_BASE_URL}/api/a2a`
        : `/api/a2a`;
      const res = await fetch(fetchUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: uuidv4(),
          method: "ApproveSkeleton",
          params: {
            metadata: {
              user_id: user.id.toString(),
              ...(localStorage.getItem("selectedOrganizationId")
                ? {
                    organization_id: localStorage.getItem(
                      "selectedOrganizationId",
                    ),
                  }
                : {}),
            },
            message: {
              parts: [
                {
                  type: "data",
                  data: {
                    session_id: sessionId,
                    request_id: requestId,
                    dpm_uuid: dpmUuid,
                  },
                },
              ],
            },
          },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      addDevLog(`Approve skeleton failed: ${err.message}`, "error");
    }
  };

  const handleUpdateSkeleton = async (requestId, newSkeleton, dpmUuid) => {
    if (!user?.id) return;
    setIsProcedureEditing(false);
    updateSkeletonStatus?.("validating_skeleton", sessionId, requestId);
    try {
      const fetchUrl = INTERPRETER_BASE_URL
        ? `${INTERPRETER_BASE_URL}/api/a2a`
        : `/api/a2a`;
      const res = await fetch(fetchUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: uuidv4(),
          method: "SkeletonEdited",
          params: {
            metadata: {
              user_id: user.id.toString(),
              ...(localStorage.getItem("selectedOrganizationId")
                ? {
                    organization_id: localStorage.getItem(
                      "selectedOrganizationId",
                    ),
                  }
                : {}),
            },
            message: {
              parts: [
                {
                  type: "data",
                  data: {
                    session_id: sessionId,
                    request_id: requestId,
                    updated_skeleton: { plan: newSkeleton },
                    dpm_uuid: dpmUuid,
                  },
                },
              ],
            },
          },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      addDevLog(`Update skeleton failed: ${err.message}`, "error");
    }
  };

  // ── Send message ────────────────────────────────────────────────────────────
  // overrideText/optionValue let an options-prompt button click send immediately,
  // without going through chatInput state (see handleOptionSelect below).
  const handleSendMessage = async (overrideText = null, optionValue = null) => {
    const queryText = (overrideText ?? chatInput).trim();
    if ((!queryText && attachedFilesInChat.length === 0) || isLoading || !user?.id)
      return;

    const attachedFiles = attachedFilesInChat.map(f => ({
      file_id: String(f.file_id),
      file_name: f.file_name,
      file_type: f.file_type,
    }));
    const userMsg = {
      type: "USER_QUERY",
      value: queryText,
      attached_files: attachedFiles.length > 0 ? attachedFiles : undefined,
    };

    // If an OPTIONS_PROMPT is still outstanding, this reply answers it: mark that
    // exact message answered (hides its buttons) and let BE know which question/
    // option this is via selected_option. Found by the `answered` flag, not array
    // position, so an unrelated message landing in between doesn't break this.
    const pendingPrompt = [...messages]
      .reverse()
      .find(
        m =>
          m.type === "AGENT_RESPONSE" &&
          m.value?.topic === "OPTIONS_PROMPT" &&
          !m.value?.answered,
      );
    const selectedOption = pendingPrompt
      ? { prompt_id: pendingPrompt.value.prompt_id, value: optionValue }
      : undefined;
    // Marked answered only on success below, so a failed request leaves the buttons retryable.
    const pendingMessageId = pendingPrompt?.value?.message_id;

    updateMessages(sessionId, [userMsg]);
    setIsLoading(true);
    setChatInput("");
    onClearAttachedFiles();

    try {
      let dpmUuid = null;
      for (let i = messages.length - 1; i >= 0; i--) {
        const uuid = messages[i]?.value?.data?.dpm_uuid;
        if (uuid) {
          dpmUuid = uuid;
          break;
        }
      }

      const chatData = await sendChatMessage({
        userQuery: queryText,
        userId: user.id,
        sessionId,
        tabType,
        fileId,
        currentFolderId,
        attachedFiles,
        dpmUuid,
        currentStep,
        selectedOption,
      });

      const agentMsg = {
        type: "AGENT_RESPONSE",
        sender: "agent",
        value: chatData.options_prompt
          ? {
              topic: "OPTIONS_PROMPT",
              answered: false,
              ...chatData.options_prompt,
            }
          : chatData.requires_clarification
            ? {
                topic: "CLARIFICATION_NEEDED",
                clarification_message: chatData.response,
              }
            : chatData.response ||
              "I'm here to help! Could you rephrase your question?",
        requires_clarification: chatData.requires_clarification || false,
        tool_used: chatData.tool_used || null,
      };
      // Mark-as-answered + append against live state, not the `messages` closure, so a concurrent SSE message survives this override.
      if (pendingMessageId) {
        updateMessages(
          sessionId,
          prev => [
            ...prev.map(m =>
              m.value?.message_id === pendingMessageId
                ? { ...m, value: { ...m.value, answered: true } }
                : m,
            ),
            agentMsg,
          ],
          true,
        );
      } else {
        updateMessages(sessionId, [agentMsg]);
      }

      const responseText =
        typeof chatData.response === "string"
          ? chatData.response
          : JSON.stringify(chatData.response ?? "");
      const dpmFilenameMatch = responseText.match(
        /\[DPM_FILENAME:\s*([^\]]+)\]/,
      );
      if (dpmFilenameMatch?.[1] && onViewModel) {
        setTimeout(() => onViewModel(dpmFilenameMatch[1].trim()), 200);
      }
    } catch (err) {
      updateMessages(sessionId, [
        {
          type: "AGENT_RESPONSE",
          sender: "agent",
          value: err.message || "Network error. Please try again.",
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptionSelect = option => {
    handleSendMessage(option.label, option.value);
  };

  const handleKeyPress = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if ((chatInput.trim() || attachedFilesInChat.length > 0) && !isLoading)
        handleSendMessage();
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Messages list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          mx: "12px",
          visibility: isFileDragOver ? "hidden" : "visible",
        }}
      >
        {messages.map((msg, index) => (
          <ChatMessageComponent
            key={msg.id || index}
            message={msg}
            onApproveSkeleton={handleApproveSkeleton}
            onUpdateSkeleton={handleUpdateSkeleton}
            onViewDpm={handleTocAction}
            currentAnchorId={currentAnchorId}
            onEditingChange={setIsProcedureEditing}
            onToggleSidebar={onToggleSidebar}
            onCancelUpload={handleCancelUpload}
            onSelectOption={handleOptionSelect}
            isLoading={isLoading}
          />
        ))}

        {isLoading && (
          <Box display='flex' alignItems='center' gap={1} sx={{ mt: 0.5 }}>
            <Fan
              sx={{
                fontSize: 18,
                color: isDark ? "#F1F4F8" : "#434950",
                animation: "spin 1.6s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(-360deg)" },
                },
              }}
            />
            <Typography
              sx={{
                color: isDark ? "#F1F4F8" : "#434950",
                fontFamily: "Inter",
                fontSize: "13px",
              }}
            >
              Thinking...
            </Typography>
          </Box>
        )}

        <Box ref={messagesEndRef} />
      </Box>

      {isProcedureEditing && (
        <Box
          sx={{
            p: "12px 8px",
            width: "100%",
            backgroundColor: "transparent",
            mb: "-10px",
            borderTop: "1px solid",
            borderColor: isDark ? "#6A707966" : "#D5D9DF",
          }}
        >
          <Typography
            sx={{
              color: isDark ? "#A8AEB6" : "#434950",
              fontFamily: "Inter",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "20px",
              letterSpacing: "0%",
            }}
          >
            You're currently editing the Procedure Outline. Save changes to
            continue.
          </Typography>
        </Box>
      )}

      {/* Input box */}
      <Box
        sx={{
          p: "16px",
          width: "calc(100% - 16px)",
          borderRadius: "12px",
          m: "12px 8px",
          background: isDark
            ? "linear-gradient(180deg, rgba(49, 54, 59, 0.2) 0%, rgba(243, 246, 250, 0.04) 99.52%)"
            : "#FFFFFF",
          boxShadow: isDark
            ? "0px 2px 5px 0px #F3F6FA26 inset"
            : "0px 1px 6px 0px #E3E3E399, 0px 4px 4px 0px #00000014",
          minHeight: "150px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <TextField
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={"Build a procedure or search files..."}
          disabled={isProcedureEditing}
          multiline
          maxRows={3}
          fullWidth
          variant='standard'
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: "16px",
              lineHeight: "20px",
              fontFamily: "Inter",
              fontWeight: 400,
              color: isDark ? "#D5D9DF" : "#434950",
              p: 0,
              mb: "10px",
            },
          }}
          sx={{
            "& .MuiInputBase-input": { p: 0 },
            "& .MuiInputBase-input::placeholder": {
              color: isDark ? "#A8AEB6" : "#6A7079",
              opacity: 1,
              fontFamily: "Inter",
              fontStyle: "italic",
            },
            "& .MuiInputBase-input.Mui-disabled": {
              color: isDark ? "#A8AEB6" : "#6A7079",
              WebkitTextFillColor: isDark ? "#A8AEB6" : "#6A7079",
              opacity: 1,
              fontStyle: "italic",
            },
          }}
        />

        {attachedFilesInChat.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              mb: "8px",
            }}
          >
            {attachedFilesInChat.map(f => (
              <AttachedFileChip
                key={f.file_id}
                {...f}
                onRemove={onRemoveAttachedFile}
              />
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={() => handleSendMessage()}
            disabled={
              (!chatInput.trim() && attachedFilesInChat.length === 0) ||
              isLoading ||
              isProcedureEditing
            }
            sx={{
              height: "16px",
              width: "16px",
              borderRadius: "4px",
              cursor: "pointer",
              p: "2px",
            }}
          >
            <SendHorizontal
              sx={{
                height: "18px",
                width: "18px",
                color: isDark ? "#F1F4F8" : "#434950",
              }}
            />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
